import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import './Sheet.css'

export type SheetVariant = 'panel' | 'dialog'

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

interface SheetProps {
  open: boolean
  onClose: () => void
  /** The accessible name and, for a panel, the heading in the header row. */
  heading: string
  /** `panel`: a bottom sheet on phones, a side panel on wide screens. `dialog`: a small sheet for a question. */
  variant?: SheetVariant
  /** Something beside the heading, such as Close. Defaults to a Close button. */
  headerEnd?: ReactNode
  /** Something at the header's start, such as the edit panel's "go to this task". */
  headerStart?: ReactNode
  /** A row under the header, such as "Task added · Add another". */
  subheader?: ReactNode
  children: ReactNode
}

/**
 * A sheet rising from the bottom on a phone. On wider screens a panel sits on
 * the right and the view behind stays usable, so only Close and Esc dismiss it
 * there; on a phone a tap outside or a swipe down anywhere on it also does.
 */
export function Sheet({ open, onClose, heading, variant = 'panel', headerEnd, headerStart, subheader, children }: SheetProps) {
  const ref = useRef<HTMLDivElement>(null)
  const closeRef = useRef(onClose)
  useEffect(() => {
    closeRef.current = onClose
  })

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onClose()
        return
      }
      // Tab stays inside while the sheet covers the view (a dialog, or any sheet on a phone)
      if (e.key === 'Tab' && ref.current && (variant === 'dialog' || window.matchMedia('(max-width: 899px)').matches)) {
        const items = ref.current.querySelectorAll<HTMLElement>(FOCUSABLE)
        if (!items.length) return
        const first = items[0]
        const last = items[items.length - 1]
        if (e.shiftKey && (document.activeElement === first || document.activeElement === ref.current)) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose, variant])

  // focus moves into the sheet when it opens, unless a field inside already took it,
  // and goes back to whatever opened the sheet when it closes
  const restoreTo = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!open) return
    restoreTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const box = ref.current
    const id = window.setTimeout(() => {
      if (box && !box.contains(document.activeElement)) box.focus()
    }, 0)
    return () => {
      window.clearTimeout(id)
      const back = restoreTo.current
      if (back?.isConnected) back.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    if (window.matchMedia('(max-width: 899px)').matches) document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // swipe down anywhere on the sheet to close it, on a phone: the sheet follows the finger and
  // either goes on down or settles back
  useEffect(() => {
    const box = ref.current
    if (!open || !box) return
    let startX = 0
    let startY = 0
    let startAt = 0
    let lastY = 0
    let lastAt = 0
    let speed = 0
    // null until the first few pixels decide: a drag of the sheet, or anything else (a scroll, a
    // drag of a checklist step, a press in a field being typed in)
    let dragging: boolean | null = null

    const xOf = (e: TouchEvent) => e.touches[0].clientX

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1 || !window.matchMedia('(max-width: 899px)').matches) {
        dragging = false
        return
      }
      const target = e.target as HTMLElement
      const body = box.querySelector('.sheet__body')
      const inBody = !!body && body.contains(target)
      const typing = !!document.activeElement && target.closest('input, textarea, [contenteditable="plaintext-only"]') === document.activeElement
      const ownGesture = !!target.closest('[data-sheet-swipe="off"]')
      dragging = (inBody && (body as HTMLElement).scrollTop > 0) || typing || ownGesture ? false : null
      startX = xOf(e)
      startY = lastY = e.touches[0].clientY
      startAt = lastAt = e.timeStamp
      speed = 0
    }

    const onMove = (e: TouchEvent) => {
      if (dragging === false) return
      const y = e.touches[0].clientY
      const dy = y - startY
      if (dragging === null) {
        const dx = xOf(e) - startX
        if (Math.abs(dy) < 8 && Math.abs(dx) < 8) return
        // a press held still first is a long press for something else, such as reordering
        dragging = dy > 0 && Math.abs(dy) > Math.abs(dx) && e.timeStamp - startAt < 300
        if (!dragging) return
        box.style.transition = 'none'
      }
      e.preventDefault()
      speed = (y - lastY) / Math.max(1, e.timeStamp - lastAt)
      lastY = y
      lastAt = e.timeStamp
      box.style.transform = `translateY(${Math.max(0, dy)}px)`
    }

    const onEnd = () => {
      if (!dragging) {
        dragging = null
        return
      }
      dragging = null
      const dy = lastY - startY
      box.style.transition = 'transform 180ms ease-out'
      if (dy > box.offsetHeight * 0.25 || (dy > 40 && speed > 0.5)) {
        box.style.transform = 'translateY(100%)'
        window.setTimeout(() => closeRef.current(), 170)
      } else {
        box.style.transform = ''
      }
    }

    box.addEventListener('touchstart', onStart, { passive: true })
    // not passive: once the sheet is being dragged, the page must not scroll or refresh as well
    box.addEventListener('touchmove', onMove, { passive: false })
    box.addEventListener('touchend', onEnd, { passive: true })
    box.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      box.removeEventListener('touchstart', onStart)
      box.removeEventListener('touchmove', onMove)
      box.removeEventListener('touchend', onEnd)
      box.removeEventListener('touchcancel', onEnd)
    }
  }, [open])

  if (!open) return null

  const isPhone = () => window.matchMedia('(max-width: 899px)').matches

  return createPortal(
    <div className={`sheet sheet--${variant}`} role="presentation">
      <button type="button" className="sheet__scrim" aria-label="Dismiss" onClick={() => (variant === 'dialog' || isPhone() ? onClose() : undefined)} tabIndex={-1} />
      <div className="sheet__box" role="dialog" aria-modal={variant === 'dialog'} aria-label={heading} ref={ref} tabIndex={-1}>
        <div className="sheet__header">
          <span className="sheet__grip" aria-hidden="true" />
          <span className="sheet__header-start">{headerStart}</span>
          <span className="sheet__heading">{heading}</span>
          <span className="sheet__header-end">
            {headerEnd ?? (
              <button type="button" className="sheet__close" onClick={onClose}>
                Close
              </button>
            )}
          </span>
        </div>
        {subheader && <div className="sheet__subheader">{subheader}</div>}
        <div className="sheet__body">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
